import { io } from 'socket.io-client';

/**
 * dashboardSocket — connection to the backend's default (`/`) namespace, used
 * only for dashboard-facing broadcasts like `sumadhura:schedule:intent-changed`.
 *
 * The existing `tvSocket` targets the `/tv` namespace (TV-app events). Phase 1
 * backend emits schedule-intent change notifications on the DEFAULT namespace
 * (see backend sumadhura.timeline.controller.js — `req.io.emit(...)`), so the
 * dashboard needs a second, parallel socket connection to hear them.
 *
 * Singleton. Connects lazily on first `connect()` call; safe to call multiple
 * times. `onIntentChanged` is reference-counted so multiple editor instances
 * can subscribe without stepping on each other.
 */
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000';

class DashboardSocket {
  constructor() {
    this.socket = null;
    this.intentListeners = new Set();
    this._boundDispatcher = null;
  }

  connect() {
    if (this.socket?.connected) return;
    if (this.socket) return; // connecting
    this.socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
    this.socket.on('connect', () => {
      console.log('✅ dashboardSocket connected -', this.socket.id);
    });
    this.socket.on('disconnect', (reason) => {
      console.log('❌ dashboardSocket disconnected:', reason);
    });
    this.socket.on('connect_error', (err) => {
      console.warn('dashboardSocket connection error:', err?.message);
    });

    this._boundDispatcher = (payload) => {
      for (const fn of this.intentListeners) {
        try {
          fn(payload);
        } catch (err) {
          console.error('dashboardSocket listener threw:', err);
        }
      }
    };
    this.socket.on('sumadhura:schedule:intent-changed', this._boundDispatcher);
  }

  onIntentChanged(fn) {
    if (typeof fn !== 'function') return;
    this.intentListeners.add(fn);
  }

  offIntentChanged(fn) {
    this.intentListeners.delete(fn);
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  disconnect() {
    if (!this.socket) return;
    this.socket.off('sumadhura:schedule:intent-changed', this._boundDispatcher);
    this.socket.disconnect();
    this.socket = null;
    this.intentListeners.clear();
  }
}

export default new DashboardSocket();
